/**
 * Feito pelo agente netexperts
 * Módulo: AnalyticsObra — seletor de datas de exibição (GET /data-exibicao)
 *
 * Lista períodos cadastrados (data_inicial a data_final).
 * - gate: seleciona e o parent confirma com "Acessar painel"
 * - compact: rascunho + botão Buscar para aplicar a consulta
 */
import { useEffect, useMemo, useState } from 'react'
import { CalendarDaysIcon, LockClosedIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { formatDate, toIsoDate } from '../../../../utils/obraAnalytics'

const EmptyState = ({ compact }) => (
    <div data-netexperts="true" className={`relative overflow-hidden bg-white border border-gray-100 rounded-2xl shadow-sm text-center ${compact ? 'p-6' : 'p-10'}`}>
        <div className="absolute inset-0 bg-gradient-to-br from-brand-yellow/5 via-white to-gray-50" />
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-brand-yellow/10 blur-2xl" />
        <div className="relative flex flex-col items-center">
            <div className="relative mb-4">
                <div className="w-16 h-16 rounded-3xl bg-gray-900 flex items-center justify-center shadow-lg">
                    <CalendarDaysIcon className="w-8 h-8 text-brand-yellow" />
                </div>
                <span className="absolute -bottom-1.5 -right-1.5 w-7 h-7 rounded-full bg-white shadow flex items-center justify-center">
                    <LockClosedIcon className="w-4 h-4 text-gray-500" />
                </span>
            </div>
            <h3 className="text-base font-bold text-gray-900">Nenhuma data disponível ainda</h3>
            <p className="text-sm text-gray-500 mt-1 max-w-md">
                Os períodos de visualização ainda não foram cadastrados. Assim que forem liberados,
                aparecerão aqui para consulta.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 text-gray-500 text-xs font-semibold">
                <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
                Aguardando liberação de datas
            </div>
        </div>
    </div>
)

const DataExibicaoPicker = ({ datas = [], loading, selectedId = '', onSelect, onApply, variant = 'gate' }) => {
    const gate = variant === 'gate'
    const [draftId, setDraftId] = useState(selectedId || '')

    useEffect(() => { setDraftId(selectedId || '') }, [selectedId])

    const appliedItem = useMemo(
        () => datas.find((d) => String(d.id) === String(selectedId)) || null,
        [datas, selectedId]
    )

    const draftItem = useMemo(
        () => datas.find((d) => String(d.id) === String(draftId)) || null,
        [datas, draftId]
    )

    const dirty = String(draftId) !== String(selectedId || '')
    const draftValido = Boolean(draftItem)

    if (loading) {
        return (
            <div data-netexperts="true" className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 flex items-center justify-center gap-3 text-sm text-gray-400">
                <span className="h-5 w-5 rounded-full border-2 border-gray-200 border-t-brand-yellow animate-spin" />
                Carregando datas disponíveis...
            </div>
        )
    }

    if (!datas.length) return <EmptyState compact={!gate} />

    const emitir = (item) => {
        if (!item) {
            onSelect?.(null)
            return
        }
        onSelect?.({
            id: item.id,
            data_inicial: toIsoDate(item.data_inicial),
            data_final: toIsoDate(item.data_final),
        })
    }

    const aplicarDraft = () => {
        if (!draftItem) return
        emitir(draftItem)
        onApply?.()
    }

    const itemExibido = gate ? draftItem : (appliedItem || draftItem)

    return (
        <div data-netexperts="true" className={`bg-white border border-gray-100 rounded-2xl shadow-sm ${gate ? 'p-6' : 'p-4'}`}>
            <div className="flex flex-wrap items-end gap-3">
                <div className="flex items-center gap-2 text-gray-700 flex-1 min-w-[200px]">
                    <div className="p-1.5 bg-gray-900 rounded-lg">
                        <CalendarDaysIcon className="w-4 h-4 text-brand-yellow" />
                    </div>
                    <div>
                        <span className="text-sm font-semibold block">Período de exibição</span>
                        <span className="text-[11px] font-semibold text-gray-400">
                            {datas.length} {datas.length === 1 ? 'período cadastrado' : 'períodos cadastrados'}
                        </span>
                    </div>
                </div>

                <div className="flex-1 min-w-[220px]">
                    <label className="block text-[11px] font-semibold text-gray-500 mb-1">Período cadastrado</label>
                    <select
                        value={draftId}
                        onChange={(e) => {
                            const id = e.target.value
                            setDraftId(id)
                            if (gate) {
                                const item = datas.find((d) => String(d.id) === String(id))
                                emitir(item || null)
                            }
                        }}
                        className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-300 focus:border-transparent bg-white"
                    >
                        <option value="">Selecione o período</option>
                        {datas.map((d) => (
                            <option key={d.id} value={String(d.id)}>
                                {formatDate(d.data_inicial)} a {formatDate(d.data_final)}
                            </option>
                        ))}
                    </select>
                </div>

                {!gate && (
                    <button
                        type="button"
                        onClick={aplicarDraft}
                        disabled={!draftValido || !dirty}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-gray-900 text-brand-yellow hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <MagnifyingGlassIcon className="w-4 h-4" /> Buscar
                    </button>
                )}
            </div>

            <p className="mt-3 text-[11px] font-semibold text-gray-400">
                {!gate && dirty ? <span className="text-amber-500">alterações não aplicadas · </span> : ''}
                {itemExibido
                    ? <>Período {gate ? 'selecionado' : 'aplicado'}: <span className="text-gray-600">{formatDate(itemExibido.data_inicial)} a {formatDate(itemExibido.data_final)}</span></>
                    : 'Escolha um dos períodos cadastrados.'}
            </p>
        </div>
    )
}

export default DataExibicaoPicker
